import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Send,
  Phone,
  Email,
  Person,
  LocationOn,
  DateRange,
  People,
  AttachMoney,
  Message,
  Favorite,
  CardGiftcard,
  Group,
  Business,
  Public,
  Museum,
  MoreHoriz,
  PersonalVideo,
  Star,
  SupportAgent,
  CheckCircle,
  Description,
} from '@mui/icons-material';
import { CustomTourRequestFormData } from '../types';
import { customTourRequestService } from '../services/firebase';
import WhatsAppButton from '../components/Common/WhatsAppButton';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const CustomTourRequestPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  
  const [pageData, setPageData] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<CustomTourRequestFormData>({
    name: '',
    email: '',
    phone: '',
    tourType: '',
    destination: '',
    duration: '',
    participantCount: 1,
    budget: '',
    preferredDates: '',
    specialRequests: '',
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      setDataLoading(true);
      setDataError(null);
      let data = await customTourRequestService.getCustomTourRequestPageData();
      
      if (!data) {
        await customTourRequestService.initializeCustomTourRequestPageData();
        data = await customTourRequestService.getCustomTourRequestPageData();
      }
      
      setPageData(data);
    } catch (err) {
      console.error('Sayfa verileri yüklenirken hata:', err);
      setDataError('Sayfa verileri yüklenirken bir hata oluştu.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.tourType || !formData.message) {
      setError(t('customTourPage.form.errors.required'));
      return;
    }

    if (!formData.email.includes('@')) {
      setError(t('customTourPage.form.errors.invalidEmail'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await customTourRequestService.submitCustomTourRequest(formData);
      
      setSuccess(t('customTourPage.form.successMessage'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        tourType: '',
        destination: '',
        duration: '',
        participantCount: 1,
        budget: '',
        preferredDates: '',
        specialRequests: '',
        message: ''
      });
    } catch (err) {
      console.error('Talep gönderilirken hata:', err);
      setError(t('customTourPage.form.errors.submitError'));
    } finally {
      setLoading(false);
    }
  };

  const getIconByName = (iconName: string): React.ReactElement => {
    const icons: Record<string, React.ReactElement> = {
      'Favorite': <Favorite />,
      'CardGiftcard': <CardGiftcard />,
      'Family': <Group />,
      'Group': <Group />,
      'Business': <Business />,
      'Public': <Public />,
      'Museum': <Museum />,
      'MoreHoriz': <MoreHoriz />,
      'Send': <Send />,
      'Phone': <Phone />,
      'Description': <Description />,
      'CheckCircle': <CheckCircle />,
      'PersonalVideo': <PersonalVideo />,
      'Star': <Star />,
      'AttachMoney': <AttachMoney />,
      'SupportAgent': <SupportAgent />
    };
    return icons[iconName] ? icons[iconName] : <MoreHoriz />;
  };

  if (dataLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (dataError || !pageData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          {dataError || t('customTourPage.form.loadError', 'Sayfa verileri yüklenemedi.')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{getTranslatedData(pageData.heroSection.title)} - HatTravel</title>
        <meta name="description" content={getTranslatedData(pageData.heroSection.description)} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh'
      }}>
        {/* Hero Section */}
        <Box sx={{ 
          background: `linear-gradient(135deg, rgba(0, 60, 148, 0.9) 0%, rgba(0, 45, 114, 0.9) 100%), url(${pageData.heroSection.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          py: { xs: 8, md: 12 },
          color: 'white',
          textAlign: 'center'
        }}>
          <Container maxWidth="lg">
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 2
              }}
            >
              {getTranslatedData(pageData.heroSection.title)}
            </Typography>
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{
                fontWeight: 300,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
                mb: 3,
                opacity: 0.9
              }}
            >
              {getTranslatedData(pageData.heroSection.subtitle)}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.8,
                opacity: 0.8
              }}
            >
              {getTranslatedData(pageData.heroSection.description)}
            </Typography>
          </Container>
        </Box>

        {/* Form Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'var(--navy-blue)',
                mb: 2
              }}
            >
              {getTranslatedData(pageData.formSection.title)}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{
                fontSize: '1.1rem',
                color: 'var(--light-gray)',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              {getTranslatedData(pageData.formSection.description)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            {/* Form Section */}
            <Box sx={{ flex: { md: 2 }, width: { xs: '100%', md: '66.666%' }, p: 1 }}>
              <Card sx={{ p: { xs: 2, md: 3 }, mb: 4 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    {t('customTourPage.form.title')}
                  </Typography>
                  
                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}
                  
                  {success && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                      {success}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <Box display="flex" flexWrap="wrap" gap={2}>
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.name')}
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          InputProps={{
                            startAdornment: <Person sx={{ mr: 1, color: 'var(--light-gray)' }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.email')}
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          InputProps={{
                            startAdornment: <Email sx={{ mr: 1, color: 'var(--light-gray)' }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.phone')}
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          InputProps={{
                            startAdornment: <Phone sx={{ mr: 1, color: 'var(--light-gray)' }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <FormControl fullWidth required>
                          <InputLabel>{t('customTourPage.form.tourType')}</InputLabel>
                          <Select
                            name="tourType"
                            value={formData.tourType}
                            onChange={handleSelectChange}
                            label={t('customTourPage.form.tourType')}
                          >
                            {pageData.formSection.tourTypes
                            .filter((type: any) => type.isActive)
                            .sort((a: any, b: any) => a.order - b.order)
                            .map((type: any) => (
                              <MenuItem key={type.id} value={getTranslatedData(type.name)}>
                                {getTranslatedData(type.name)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.destination')}
                          name="destination"
                          value={formData.destination}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: <LocationOn sx={{ mr: 1, color: 'var(--light-gray)' }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.duration')}
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: <DateRange sx={{ mr: 1, color: 'var(--light-gray)' }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.participantCount')}
                          name="participantCount"
                          type="number"
                          value={formData.participantCount}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: <People sx={{ mr: 1, color: 'var(--light-gray)' }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.budget')}
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          placeholder={t('customTourPage.form.budgetPlaceholder')}
                          InputProps={{
                            startAdornment: <Typography sx={{ mr: 1, color: 'var(--light-gray)', fontSize: '1.2rem', fontWeight: 600 }}>₺</Typography>
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.preferredDates')}
                          name="preferredDates"
                          value={formData.preferredDates}
                          onChange={handleInputChange}
                          placeholder={t('customTourPage.form.preferredDatesPlaceholder')}
                          InputProps={{
                            startAdornment: <DateRange sx={{ mr: 1, color: 'var(--light-gray)' }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.specialRequests')}
                          name="specialRequests"
                          value={formData.specialRequests}
                          onChange={handleInputChange}
                          multiline
                          rows={3}
                          placeholder={t('customTourPage.form.specialRequestsPlaceholder')}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <TextField
                          fullWidth
                          label={t('customTourPage.form.message')}
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          multiline
                          rows={4}
                          required
                          placeholder={t('customTourPage.form.messagePlaceholder')}
                          InputProps={{
                            startAdornment: <Message sx={{ mr: 1, color: 'var(--light-gray)', mt: 1 }} />
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ flex: '1 1 45%', minWidth: 260 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={loading}
                          sx={{
                            py: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            backgroundColor: 'var(--navy-blue)',
                            '&:hover': {
                              backgroundColor: 'var(--dark-blue)'
                            }
                          }}
                        >
                          {loading ? (
                            <>
                              <CircularProgress size={24} sx={{ mr: 2 }} />
                              {t('customTourPage.form.sending')}
                            </>
                          ) : (
                            <>
                              <Send sx={{ mr: 2 }} />
                              {t('customTourPage.form.submit')}
                            </>
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </form>
                </CardContent>
              </Card>
            </Box>

            {/* Sidebar Section */}
            <Box sx={{ flex: 1, width: { xs: '100%', md: '33.333%' }, p: 1 }}>
              {/* Tour Types */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('customTourPage.tourTypesTitle')}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={2}>
                    {pageData.formSection.tourTypes
                    .filter((type: any) => type.isActive)
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((type: any) => (
                      <Chip
                        key={type.id}
                        label={getTranslatedData(type.name)}
                        icon={getIconByName(type.icon)}
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>

              {/* Contact Info */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('customTourPage.contactTitle')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Phone sx={{ color: 'var(--navy-blue)' }} />
                      <Typography variant="body2">
                        +90 555 123 45 67
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Email sx={{ color: 'var(--navy-blue)' }} />
                      <Typography variant="body2">
                        info@hattravel.com
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Container>

        {/* Process Section */}
        <Box sx={{ backgroundColor: 'var(--light-gray-white)', py: { xs: 4, md: 8 } }}>
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: 'var(--navy-blue)',
                  mb: 2
                }}
              >
                {getTranslatedData(pageData.processSection.title)}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{
                  fontSize: '1.1rem',
                  color: 'var(--light-gray)',
                  maxWidth: '600px',
                  mx: 'auto'
                }}
              >
                {getTranslatedData(pageData.processSection.description)}
              </Typography>
            </Box>

            <Box display="flex" flexWrap="wrap" gap={2}>
              {pageData.processSection.steps
              .filter((step: any) => step.isActive)
              .sort((a: any, b: any) => a.order - b.order)
              .map((step: any, index: number) => (
                <Box key={step.id} sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' }, minWidth: { xs: 0, sm: 180 }, p: 1 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Avatar
                      sx={{
                        width: 90,
                        height: 90,
                        backgroundColor: 'var(--navy-blue)',
                        mx: 'auto',
                        mb: 3,
                        '& .MuiSvgIcon-root': {
                          fontSize: '2.2rem',
                          color: 'white'
                        }
                      }}
                    >
                      {getIconByName(step.icon)}
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {getTranslatedData(step.title)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getTranslatedData(step.description)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Container>
        </Box>

        {/* Why Choose Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              gutterBottom
              sx={{
                fontWeight: 700,
                color: 'var(--navy-blue)',
                mb: 2
              }}
            >
              {getTranslatedData(pageData.whyChooseSection.title)}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{
                fontSize: '1.1rem',
                color: 'var(--light-gray)',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              {getTranslatedData(pageData.whyChooseSection.description)}
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={2}>
            {pageData.whyChooseSection.features
            .filter((feature: any) => feature.isActive)
            .sort((a: any, b: any) => a.order - b.order)
            .map((feature: any) => (
              <Box key={feature.id} sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 22%' }, minWidth: { xs: 0, sm: 180 }, p: 1 }}>
                <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
                  <CardContent>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        backgroundColor: 'var(--navy-blue)',
                        mx: 'auto',
                        mb: 3,
                        '& .MuiSvgIcon-root': {
                          fontSize: '2rem',
                          color: 'white'
                        }
                      }}
                    >
                      {getIconByName(feature.icon)}
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {getTranslatedData(feature.title)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {getTranslatedData(feature.description)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
      
      <WhatsAppButton />
    </>
  );
};

export default CustomTourRequestPage; 